import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { trpcClient } from "@/lib/trpc";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, Calendar, Clock, Cpu, User, AlertTriangle, Zap, Lock, Unlock } from "lucide-react";
import { formatNumber, cn } from "@/lib/utils";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { EntityLink } from "@/components/shared/EntityLink";
import {
  getScheduleConflicts,
  describeScheduleConflict,
  type ScheduleConflict,
} from "@/lib/schedule-conflicts";

import { toVnDateTimeLocal, describeOverlap, OVERLAP_PERCENT_MAX } from "@apv-erp/status";
export interface RoutingStepRef {
  id: string;
  stepId?: string;
  name?: string;
  type?: string;
  status?: string;
  resourceId?: string | null;
  machineId?: string | null;
  operatorId?: string | null;
  qtyPlan?: number;
  standardQuantity?: number;
  overlapPercent?: number | null;
  plannedStartDate?: string | null;
  plannedEndDate?: string | null;
  /** Lịch đã khóa: bộ lập lịch tự động không dời bước này */
  scheduleLocked?: boolean;
  scheduleNote?: string | null;
  productionOrder?: {
    orderId?: string;
    productName?: string;
    customerName?: string;
  };
  poCode?: string;
  productName?: string;
}

interface MachineAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  step: RoutingStepRef | null;
  onSuccess?: () => void;
}

export function getCompatibleMachineTypes(step: RoutingStepRef | null): string[] | undefined {
  if (!step) return undefined;
  const defaultMachineType = (step as any).process?.defaultMachineType;
  if (defaultMachineType) {
    return [defaultMachineType];
  }

  const raw = `${step.type || ""} ${(step as any).name || ""} ${step.stepId || ""}`.toLowerCase();

  if (raw.includes("in") || raw.includes("offset") || raw.includes("print")) {
    return ["OFFSET_PRINTER", "FLEXO_PRINTER"];
  }
  if (raw.includes("cắt") || raw.includes("xén") || raw.includes("cut")) {
    return ["PAPER_CUTTER"];
  }
  if (raw.includes("cán") || raw.includes("màng") || raw.includes("laminat")) {
    return ["LAMINATOR", "COATER"];
  }
  if (raw.includes("bế") || raw.includes("die") || raw.includes("dập")) {
    return ["DIE_CUTTER"];
  }
  if (raw.includes("dán") || raw.includes("hộp") || raw.includes("gluing") || raw.includes("glue")) {
    return ["FOLDER_GLUER"];
  }
  if (raw.includes("ép kim") || raw.includes("foil")) {
    return ["HOT_FOIL"];
  }
  if (raw.includes("lụa") || raw.includes("screen")) {
    return ["SILK_SCREEN"];
  }
  if (raw.includes("đóng gói") || raw.includes("packing") || raw.includes("kcs")) {
    return ["PACKING"];
  }
  return undefined;
}

export function MachineAssignmentModal({
  isOpen,
  onClose,
  step,
  onSuccess,
}: MachineAssignmentModalProps) {
  const queryClient = useQueryClient();
  const [selectedMachine, setSelectedMachine] = useState<string>("");
  const [selectedOperator, setSelectedOperator] = useState<string>("");
  const [plannedStartDate, setPlannedStartDate] = useState<string>("");
  const [plannedEndDate, setPlannedEndDate] = useState<string>("");
  const [overlapPercent, setOverlapPercent] = useState<number | string>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filterCompatibleOnly, setFilterCompatibleOnly] = useState<boolean>(true);
  const [isLocked, setIsLocked] = useState<boolean>(false);
  const [conflicts, setConflicts] = useState<ScheduleConflict[] | null>(null);

  const compatibleTypes = React.useMemo(() => getCompatibleMachineTypes(step), [step]);

  // Máy đúng loại của công đoạn: máy chủ suy từ nhóm công đoạn (cùng quy tắc với nhật ký sản xuất, 03/11/2026);
  // công đoạn chưa lưu (chưa có id) thì dùng loại máy suy từ tên công đoạn như trước
  const machineParams = React.useMemo(() => {
    if (!filterCompatibleOnly) return undefined;
    if (step?.id) return { routingStepId: step.id };
    if (!compatibleTypes || compatibleTypes.length === 0) return undefined;
    return compatibleTypes.length === 1 ? { type: compatibleTypes[0] } : { type: { in: compatibleTypes } };
  }, [filterCompatibleOnly, compatibleTypes, step?.id]);

  useEffect(() => {
    if (step) {
      setSelectedMachine(step.machineId || step.resourceId || "");
      setSelectedOperator(step.operatorId || "");
      setPlannedStartDate(
        step.plannedStartDate ? toVnDateTimeLocal(step.plannedStartDate) : ""
      );
      setPlannedEndDate(
        step.plannedEndDate ? toVnDateTimeLocal(step.plannedEndDate) : ""
      );
      setOverlapPercent(
        step.overlapPercent !== undefined && step.overlapPercent !== null
          ? step.overlapPercent
          : 0
      );
      setIsLocked(Boolean(step.scheduleLocked));
      setConflicts(null);
    }
  }, [step]);

  const handleSave = async (confirmOverlap = false) => {
    if (!step) return;
    setIsSubmitting(true);
    try {
      const payload: Record<string, any> = {
        machineId: selectedMachine || null,
        resourceId: selectedMachine || null,
        operatorId: selectedOperator || null,
        plannedStartDate: plannedStartDate
          ? new Date(plannedStartDate).toISOString()
          : null,
        plannedEndDate: plannedEndDate
          ? new Date(plannedEndDate).toISOString()
          : null,
        overlapPercent: Number(overlapPercent) || 0,
        ...(confirmOverlap ? { confirmOverlap: true } : {}),
        // Bỏ chọn "Khóa lịch" → mở khóa (bộ lập lịch được xếp lại bước này)
        ...(step.scheduleLocked && !isLocked ? { scheduleLocked: false } : {}),
        ...(!step.scheduleLocked && isLocked ? { scheduleLocked: true } : {}),
      };

      await trpcClient.productionOrders.updateRoutingStepInline.mutate({
        stepId: step.id,
        data: payload,
      });

      toast.success(
        confirmOverlap
          ? "Đã lưu điều độ và khóa lịch công đoạn (chấp nhận chồng lịch)."
          : "Đã cập nhật điều độ lịch máy thành công!"
      );
      queryClient.invalidateQueries({ queryKey: ["stage-work-orders"] });
      queryClient.invalidateQueries({ queryKey: ["production-order"] });
      queryClient.invalidateQueries({ queryKey: ["production-orders"] });
      
      onSuccess?.();
      onClose();
    } catch (error: any) {
      // Chồng lịch với việc khác trên cùng máy: hỏi xác nhận, không phải lỗi
      const overlap = getScheduleConflicts(error);
      if (overlap) {
        setConflicts(overlap);
        return;
      }
      console.error("Lỗi khi điều độ máy:", error);
      toast.error(error.message || "Lỗi khi cập nhật điều độ lịch máy");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!step) return null;

  const poCode = step.productionOrder?.orderId || step.poCode || "LSX";
  const productName = step.productionOrder?.productName || step.productName || "";
  const stepName = step.name || step.type || "Công đoạn";
  const quantity = step.standardQuantity || step.qtyPlan || 0;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base text-slate-900">
            <Cpu className="w-5 h-5 text-teal-600" />
            <span>Điều Độ &amp; Gán Lịch Máy Sản Xuất</span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-[minmax(0,1fr)] gap-4 py-2 text-xs">
          {/* Box tóm tắt thông tin công đoạn */}
          <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
            <div className="flex items-center justify-between font-bold text-slate-800 text-sm">
              {/* Mã lệnh là link sang trang Lệnh sản xuất (xem lệnh cha của công đoạn) */}
              <EntityLink entityType="productionOrder" code={poCode} className="font-mono text-sm font-bold" />
              <span className="text-xs font-semibold text-slate-600">
                SL: {formatNumber(quantity)}
              </span>
            </div>
            {productName && (
              <div className="text-xs text-slate-600 mt-0.5 break-words">
                {productName}
              </div>
            )}
            <div className="text-xs text-slate-500 mt-1 flex items-center gap-1.5 pt-1 border-t border-slate-200/60">
              <span>Công đoạn:</span>
              <span className="font-bold text-slate-800 bg-white px-2 py-0.5 rounded border border-slate-200">
                {stepName}
              </span>
            </div>
            {step.scheduleNote && (
              <div
                className="mt-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
                data-testid="dispatch-schedule-note"
              >
                {step.scheduleNote}
              </div>
            )}
          </div>

          {/* Chọn Máy Móc */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="font-semibold text-slate-700 flex items-center gap-1">
                <Cpu className="w-3.5 h-3.5 text-teal-600" />
                Chọn máy / thiết bị thực hiện:
              </label>
              {compatibleTypes && compatibleTypes.length > 0 && (
                <button
                  type="button"
                  onClick={() => setFilterCompatibleOnly(!filterCompatibleOnly)}
                  className="text-[11px] text-teal-700 hover:underline flex items-center gap-1 cursor-pointer"
                >
                  <span
                    className={cn(
                      "w-3 h-3 rounded-xs border flex items-center justify-center",
                      filterCompatibleOnly
                        ? "bg-teal-600 border-teal-600 text-white"
                        : "border-slate-300"
                    )}
                  >
                    {filterCompatibleOnly && <Check className="w-2.5 h-2.5" />}
                  </span>
                  Chỉ máy tương thích
                </button>
              )}
            </div>

            <SharedCombobox
              type="machine"
              value={selectedMachine}
              onChange={(val) => setSelectedMachine(val || "")}
              placeholder="Chọn máy chạy công đoạn..."
              params={machineParams}
            />
          </div>

          {/* Chọn Nhân Sự Vận Hành */}
          <div className="space-y-1.5">
            <label className="font-semibold text-slate-700 flex items-center gap-1">
              <User className="w-3.5 h-3.5 text-indigo-600" />
              Thợ máy / Trưởng ca phụ trách:
            </label>
            <SharedCombobox
              type="user"
              params={{ scope: 'production' }} // nhân sự theo quy trình (03/11/2026)
              value={selectedOperator}
              onChange={(val) => setSelectedOperator(val || "")}
              placeholder="Chọn nhân viên vận hành..."
            />
          </div>

          {/* Lịch Ngày Giờ Bắt Đầu & Kết Thúc */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="font-semibold text-slate-700 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5 text-slate-500" />
                Dự kiến bắt đầu:
              </label>
              <input
                type="datetime-local"
                value={plannedStartDate}
                onChange={(e) => setPlannedStartDate(e.target.value)}
                className="w-full text-xs font-mono border border-slate-200 rounded-md px-2.5 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </div>

            <div className="space-y-1.5">
              <label className="font-semibold text-slate-700 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5 text-slate-500" />
                Hạn hoàn thành:
              </label>
              <input
                type="datetime-local"
                value={plannedEndDate}
                onChange={(e) => setPlannedEndDate(e.target.value)}
                className="w-full text-xs font-mono border border-slate-200 rounded-md px-2.5 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </div>
          </div>

          {/* Tỷ Lệ Gối Đầu APS */}
          <div className="space-y-1.5 bg-purple-50/50 p-2.5 rounded-lg border border-purple-100">
            <div className="flex items-center justify-between">
              <label className="font-semibold text-purple-900 flex items-center gap-1">
                <Zap className="w-3.5 h-3.5 text-purple-600" />
                Tỷ lệ gối đầu APS (% Overlap):
              </label>
              <span className="font-mono font-bold text-purple-700 text-xs">
                {overlapPercent}%
              </span>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="0"
                max={String(OVERLAP_PERCENT_MAX)}
                step="5"
                value={overlapPercent}
                onChange={(e) => setOverlapPercent(Number(e.target.value))}
                className="flex-1 accent-purple-600 cursor-pointer"
              />
            </div>
            <p className="text-[10px] text-purple-600 leading-tight" data-testid="dispatch-overlap-hint">
              {describeOverlap(overlapPercent)} (Áp dụng cho công đoạn này so với công đoạn liền trước.)
            </p>
          </div>

          {/* Khóa lịch: bộ lập lịch tự động không dời công đoạn đã khóa */}
          <label
            className={cn(
              "flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer",
              isLocked ? "bg-slate-100 border-slate-300" : "bg-white border-slate-200"
            )}
          >
            <input
              type="checkbox"
              checked={isLocked}
              onChange={(e) => setIsLocked(e.target.checked)}
              className="mt-0.5 accent-slate-700"
              data-testid="dispatch-lock-checkbox"
            />
            <span className="text-[11px] text-slate-700 leading-tight">
              <span className="font-semibold flex items-center gap-1">
                {isLocked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
                Khóa lịch công đoạn này
              </span>
              Lập lịch tự động sẽ không dời giờ/máy của công đoạn đã khóa. Sửa tay giờ chạy sẽ tự khóa.
            </span>
          </label>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Hủy
          </Button>
          <Button
            type="button"
            size="sm"
            className="bg-teal-600 hover:bg-teal-700 font-semibold"
            onClick={() => handleSave(false)}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Đang lưu..." : "Lưu Điều Độ"}
          </Button>
        </DialogFooter>
      </DialogContent>

      <ConfirmDialog
        open={Boolean(conflicts)}
        onOpenChange={(open) => !open && setConflicts(null)}
        title="Máy đã có việc trong khung giờ này"
        description={
          <span className="block space-y-1.5 text-left" data-testid="dispatch-overlap-warning">
            <span className="block">Công đoạn sẽ chồng lịch với:</span>
            {(conflicts || []).map((c) => (
              <span key={c.stepId} className="block font-mono text-xs text-rose-700">
                • {describeScheduleConflict(c)}
              </span>
            ))}
            <span className="block text-xs text-slate-600">
              Vẫn lưu thì công đoạn được khóa lịch (lập lịch tự động không dời) và máy chạy chồng — cần sắp lại tay.
            </span>
          </span>
        }
        confirmText="Vẫn lưu và khóa lịch"
        cancelText="Quay lại sửa"
        variant="destructive"
        isLoading={isSubmitting}
        onConfirm={() => handleSave(true)}
      />
    </Dialog>
  );
}

export default MachineAssignmentModal;
